import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Drawer } from 'antd';
import { getDuplicates, mergeUsers } from '../../apis/SettingApis';
import { getCombinedUser } from '../../apis/UserApi';
import UserForm from '../entities/User/userForm';

const DuplicateManager = () => {
    const [clusters, setClusters] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [fetchingUser, setFetchingUser] = useState(false);
    const toast = React.useRef(null);

    useEffect(() => {
        loadDuplicates();
    }, []);

    const loadDuplicates = async () => {
        setLoading(true);
        try {
            const response = await getDuplicates();
            const users = response.data;
            
            // Group users by duplicate fields
            const groups = {};
            users.forEach(user => {
                const fields = [
                    { value: user.mobile, type: 'Phone' },
                    { value: user.cnic, type: 'CNIC' },
                    { value: user.email, type: 'Email' }
                ];
                
                fields.forEach(field => {
                    const k = field.value;
                    if (!k || k.length < 5 || k.startsWith('$') || k.startsWith('999')) return;
                    
                    const groupKey = `${field.type}: ${k}`;
                    if (!groups[groupKey]) groups[groupKey] = [];
                    if (!groups[groupKey].find(u => u.id === user.id)) {
                        groups[groupKey].push(user);
                    }
                });
            });

            // Filter out groups with only 1 user and convert to array
            const clusterList = Object.keys(groups)
                .filter(key => groups[key].length > 1)
                .map(key => ({
                    key,
                    users: groups[key]
                }));

            setClusters(clusterList);
            // Auto-expand all clusters
            if (clusterList.length > 0) {
                setExpandedRows(clusterList);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load duplicates' });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (userId) => {
        setFetchingUser(true);
        try {
            const response = await getCombinedUser(userId);
            setEditingUser(response.data);
            setEditDrawerOpen(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch user details' });
        } finally {
            setFetchingUser(false);
        }
    };

    const handleMerge = async (masterId, duplicateId) => {
        if (!window.confirm(`Are you sure you want to merge ID:${duplicateId} into ID:${masterId}? This will delete record ${duplicateId}.`)) {
            return;
        }
        try {
            await mergeUsers(masterId, duplicateId);
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Users merged successfully' });
            loadDuplicates();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.response?.data || 'Merge failed' });
        }
    };

    const userTemplate = (cluster) => {
        return (
            <div className="p-3 bg-gray-50">
                <DataTable value={cluster.users} responsiveLayout="scroll" className="p-datatable-sm shadow-1">
                    <Column field="id" header="ID" style={{ width: '5rem' }}></Column>
                    <Column field="name" header="Name"></Column>
                    <Column field="mobile" header="Mobile"></Column>
                    <Column field="cnic" header="CNIC"></Column>
                    <Column field="userType" header="Role" body={(u) => <Tag value={u.userType} severity="info"></Tag>}></Column>
                    <Column header="Actions" body={(rowData) => {
                        const others = cluster.users.filter(u => u.id !== rowData.id);
                        return (
                            <div className="flex gap-2">
                                <Button 
                                    icon="pi pi-pencil" 
                                    className="p-button-rounded p-button-info p-button-sm" 
                                    tooltip="Edit this record"
                                    loading={fetchingUser && editingUser?.id === rowData.id}
                                    onClick={() => handleEdit(rowData.id)}
                                />
                                <div className="flex flex-column gap-1">
                                    {others.map(other => (
                                        <Button 
                                            key={other.id}
                                            label={`Merge ${other.id} here`} 
                                            icon="pi pi-sign-in"
                                            className="p-button-xs p-button-warning"
                                            onClick={() => handleMerge(rowData.id, other.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    }}></Column>
                </DataTable>
            </div>
        );
    };

    return (
        <div className="p-4">
            <Toast ref={toast} />
            
            <Drawer
                title="Edit User Details"
                width={500}
                onClose={() => setEditDrawerOpen(false)}
                open={editDrawerOpen}
            >
                {editingUser && (
                    <UserForm
                        onSubmitSuccess={() => {
                            setEditDrawerOpen(false);
                            loadDuplicates();
                            toast.current.show({ severity: 'success', summary: 'Updated', detail: 'User updated successfully' });
                        }}
                        hide={() => setEditDrawerOpen(false)}
                        editMode={true}
                        editorValues={editingUser}
                    />
                )}
            </Drawer>

            <Card title="Duplicate Record Manager" subTitle="Audit and resolve redundant user accounts.">
                <p className="text-gray-600 mb-4">
                    The groups below share the same <b>Phone, CNIC, or Email</b>. You can <b>Edit</b> a record to fix errors or <b>Merge</b> a duplicate into a master.
                </p>
                
                {clusters.length === 0 && !loading && (
                    <div className="p-text-center p-4">
                        <Tag severity="success" value="Excellent! No duplicates found in the system."></Tag>
                    </div>
                )}

                <DataTable 
                    value={clusters} 
                    loading={loading}
                    expandedRows={expandedRows} 
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={userTemplate}
                    dataKey="key"
                    paginator rows={10}
                    className="shadow-2"
                >
                    <Column expander style={{ width: '3em' }} />
                    <Column field="key" header="Conflicting Value" body={(rowData) => (
                        <span className="font-bold text-primary">{rowData.key}</span>
                    )} />
                    <Column header="Cluster Size" body={(rowData) => (
                        <Tag severity="danger" value={`${rowData.users.length} Records`}></Tag>
                    )} />
                </DataTable>
            </Card>
        </div>
    );
};

export default DuplicateManager;
